import type { ScenarioParamDef, SimMonthlySource, SimSettings } from '../FloodSimSettings.types';

/**
 * Hover help for every field on the card. Each sentence is read from the
 * simulator's own code (ExtServices/StageFloodSim_UDT), not from what the
 * field name suggests — including the one that says a field is not read.
 * Mike, 2026-08-28: "interval (ms), Event Interval (ms), loop enabled, etc
 * all need hover over help pop ups ... I am unsure without descriptions to
 * help remind me what the functions do."
 */
export const FIELD_HELP = {
  enabled:
    'Master switch. On: the StageFloodSim service writes invented readings for this device on a ' +
    'clock, and RI_Device.sim_mode follows (1 = donor, 2 = scenario). Off: the service leaves ' +
    'the device alone and writes nothing for it. Save stores the configuration; the service ' +
    're-reads it on its next config refresh (60 s by default) or at once from the ProjectDocs ' +
    'Flood Sim console\u2019s Refresh.',
  sim_type:
    'Donor: replay another device\u2019s stored (or generated) history as this device\u2019s readings, ' +
    'at the history\u2019s own spacing. Scenario: draw a named curve \u2014 cross a threshold, a storm, ' +
    'a dam break \u2014 computed from this device\u2019s own thresholds, one reading per Interval.',
  interval_ms:
    'Scenario mode: how often one new reading is written \u2014 one tick. A reading a minute ' +
    'suits a demonstration; the real fleet reports about every 10\u201315 minutes in normal ' +
    'water. The simulator floors every tick at 10 s (STAGE_SIM_MIN_INTERVAL_MS). Donor mode ' +
    'does not use it: replayed rows keep their original spacing (a long gap in the history ' +
    'is capped so the replay does not stall).',
  event_interval_ms:
    'Scenario mode, during an event: the node’s FAST-mode tick — flat the whole time the ' +
    'level is rising or holding at or above threshold 1, exactly like the real hardware ' +
    '(10–15 s, per node). While the level is FALLING the tick stretches by tier — 2× above ' +
    'threshold 3, 4× above threshold 2, 8× above threshold 1 — so the recession tapers ' +
    'off on its own; below threshold 1 the normal Interval applies. Identical readings never ' +
    'insert a row (the last row’s counter increments), and the simulator floors every tick ' +
    'at 10 s, so event density cannot bloat the data table. Donor mode does not use it.',
  sim_end_time:
    'When the demo should stop, in your own timezone. Both modes honour it — the service ' +
    'checks it before every tick it schedules. At that moment the device stops writing readings and ' +
    'shows as idle, with the configuration left intact; clear the field to start it again. Leave it ' +
    'empty to run until the sim is switched off.',
  loop_enabled:
    'Donor mode only. On (also the default when unset): when the replay reaches the end of its ' +
    'window it starts again from the first row. Off: it stops at the end and the device goes ' +
    'quiet. Scenario mode ignores it \u2014 whether a scenario repeats is part of the scenario ' +
    '(branch_sweep repeats; a crossing holds its level). This is not the on/off switch for the ' +
    'demo \u2014 that is Enabled, and the status line above says which state it is in.',
  donor_source:
    'archive: the donor device\u2019s real stored history (RI_Device_Data_Archive). custom_table: a ' +
    'copy staged in a named table (needs Source Table). generator: synthetic but realistic rows ' +
    'built from this device\u2019s thresholds \u2014 the choice on DEV, where the archive is thin.',
  donor_source_table: 'The table to read donor rows from when Donor Source is custom_table.',
  donor_uid:
    'The device whose history is replayed onto this one. With the generator it only seeds the ' +
    'random stream \u2014 the same uid always draws the same curve.',
  donor_ts_start:
    'Start of the slice of the donor\u2019s history to replay. Ignored when a Monthly Source is set.',
  donor_ts_end:
    'End of the slice of the donor\u2019s history to replay. Ignored when a Monthly Source is set.',
  monthly_year:
    'Replay the donor\u2019s readings from this year and month instead of a fixed window. Each ' +
    'real-world month the replay restarts at day 1 of the source month and plays as many days as ' +
    'both months have.',
  monthly_month: 'The source month, 1\u201312. See Monthly Source \u2014 Year.',
  scenario_name:
    'The curve this device draws. The summary under the picker says what each one does; the ' +
    'parameters below shape it. A parameter whose default is 0 is derived from this device\u2019s ' +
    'own thresholds, so a scenario works on any device without retuning.',
  global_action_id:
    'Reserved \u2014 not read today. The design: take the thresholds the scenario crosses from this ' +
    'Global Action\u2019s rule instead of the device\u2019s own depthDet1/2/3, so a run lines up with ' +
    'what the action fires on. The simulator\u2019s loader for it is still a stub that returns ' +
    'nothing, so every scenario uses the device\u2019s own thresholds whatever is entered here.',
  base_donor_uid:
    'Optional. Use one real reading \u2014 this device\u2019s row at Base Donor Timestamp \u2014 as the ' +
    'base for every scenario reading (battery, temperatures, image fields ...); the scenario only ' +
    'rewrites the depths. Blank: a generated healthy-gauge base is used.',
  base_donor_ts: 'The timestamp of the base reading. Used only with Base Donor UID.',
  routing_upstream:
    'Optional. The Stage device immediately upstream on the same waterway. Choosing one puts this ' +
    'device on that device\u2019s chain, copies its scenario (a chain runs one scenario), and delays ' +
    'the same curve here by the travel time below \u2014 the flood reaches this gauge after it reaches ' +
    'the upstream one. None takes the device off its chain. The travel time is an ESTIMATE from simple ' +
    'arithmetic for the simulation; it is not a forecast.',
  routing_speed:
    'How fast the water moves between the two devices, in miles per hour. Travel time = the ' +
    'straight-line distance between the two pins \u00f7 this speed \u2014 a simple model; a real river ' +
    'is longer than the straight line, so lower the speed to allow for it. Rivers typically run ' +
    '1\u20135 mph; a flash flood in a steep channel, more. The result is an ESTIMATE for the simulation ' +
    'and may not hold up in the real world.',
} as const;

/**
 * Shown wherever a waterway time is printed — the card, the map, the help.
 * Mike, 2026-08-28: "make abundantly clear that all times are estimates based
 * on some simple math and may not hold up in the real world, this is a
 * simulation tool only."
 */
export const SIMULATION_DISCLAIMER =
  'SIMULATION TOOL ONLY. Every time shown here is an estimate from simple arithmetic \u2014 the ' +
  'straight-line distance between two pins \u00f7 one flow speed. A real river is longer than the ' +
  'straight line, its speed changes along the way, and rain, terrain and debris are not in the ' +
  'model. These figures may not hold up in the real world. Do not use them for a real flood warning.';

export type DonorDateField = { label: string; field: 'donor_ts_start' | 'donor_ts_end'; help: string };
export const donorDateFields: DonorDateField[] = [
  { label: 'Window Start', field: 'donor_ts_start', help: FIELD_HELP.donor_ts_start },
  { label: 'Window End', field: 'donor_ts_end', help: FIELD_HELP.donor_ts_end },
];

export type MonthlyField = {
  label: string;
  field: keyof SimMonthlySource;
  min?: number;
  max?: number;
  placeholder?: string;
  help: string;
};
export const monthlyFields: MonthlyField[] = [
  {
    label: 'Monthly Source — Year',
    field: 'year',
    placeholder: 'e.g. 2025',
    help: FIELD_HELP.monthly_year,
  },
  {
    label: 'Monthly Source — Month',
    field: 'month',
    min: 1,
    max: 12,
    placeholder: '1 – 12',
    help: FIELD_HELP.monthly_month,
  },
];

export type ScenarioNullableField = {
  label: string;
  field: 'global_action_id' | 'base_donor_uid';
  help: string;
};
export const scenarioNullableFields: ScenarioNullableField[] = [
  { label: 'Global Action ID', field: 'global_action_id', help: FIELD_HELP.global_action_id },
  { label: 'Base Donor UID', field: 'base_donor_uid', help: FIELD_HELP.base_donor_uid },
];

/**
 * FALLBACK ONLY. The card renders the picker from the backend catalogue
 * (`useGetStageSimScenariosQuery`), which carries every registered scenario
 * with its summary and parameter descriptions. This list is what the card
 * shows if that read fails, so it is deliberately the eight threshold
 * exercisers and nothing newer — a second full copy would be a list to drift.
 */
/**
 * One line per fallback scenario, so the picker explains itself even when the
 * catalogue read fails (an older backend, or one that has not been restarted).
 * Same words as the simulator's own SUMMARIES in
 * ExtServices/StageFloodSim_UDT/src/api/scenarioCatalog.ops.ts — the catalogue,
 * when it answers, is the source and overrides these.
 */
export const SCENARIO_SUMMARIES: Record<string, string> = {
  hold_below: 'Sits below the first threshold. A quiet baseline — nothing should fire.',
  cross_det1: 'Holds low, then crosses threshold 1 and stays above it.',
  cross_det2: 'Holds low, then crosses threshold 2 and stays above it.',
  cross_det3: 'Holds low, then crosses threshold 3 and stays above it.',
  flash: 'Jumps straight past threshold 3 in one tick — the flash-flood path.',
  gradual: 'Climbs linearly from 0 through every threshold to a peak above threshold 3.',
  receding: 'Falls linearly from a peak above threshold 3 back to zero.',
  branch_sweep:
    'Steps up through every threshold and back down — below, 1, 2, 3, 2, 1, below — then repeats. ' +
    'The one run that exercises every branch of a Global Action.',
};

export const scenarioParamDefs: Record<string, ScenarioParamDef[]> = {
  hold_below: [{ key: 'ratio', label: 'Ratio', type: 'float', default: 0.5 }],
  cross_det1: [
    { key: 'ticksBeforeCross', label: 'Ticks Before Cross', type: 'int', default: 5 },
    { key: 'overshoot', label: 'Overshoot', type: 'float', default: 1 },
  ],
  cross_det2: [
    { key: 'ticksBeforeCross', label: 'Ticks Before Cross', type: 'int', default: 5 },
    { key: 'overshoot', label: 'Overshoot', type: 'float', default: 1 },
  ],
  cross_det3: [
    { key: 'ticksBeforeCross', label: 'Ticks Before Cross', type: 'int', default: 5 },
    { key: 'overshoot', label: 'Overshoot', type: 'float', default: 1 },
  ],
  flash: [{ key: 'overshoot', label: 'Overshoot', type: 'float', default: 20 }],
  gradual: [
    { key: 'totalTicks', label: 'Total Ticks', type: 'int', default: 60 },
    { key: 'overshoot', label: 'Overshoot', type: 'float', default: 5 },
  ],
  receding: [
    { key: 'totalTicks', label: 'Total Ticks', type: 'int', default: 60 },
    { key: 'overshoot', label: 'Overshoot', type: 'float', default: 10 },
  ],
  branch_sweep: [
    { key: 'ticksPerStep', label: 'Ticks Per Step', type: 'int', default: 3 },
    { key: 'overshoot', label: 'Overshoot', type: 'float', default: 1 },
  ],
};

export const DEFAULT_SIM: SimSettings = {
  enabled: 0,
  sim_type: 'donor',
  interval_ms: 60000, // 1 minute
  event_interval_ms: 5000,
  // Overwritten by makeDefaultSim() with an end time relative to NOW; a
  // literal here would be stale for the life of the page.
  sim_end_time: null,
  loop_enabled: 0,
  donor_source: 'generator',
  donor_source_table: null,
  donor_uid: null,
  donor_ts_start: null,
  donor_ts_end: null,
  monthly_source: null,
  scenario_name: 'branch_sweep',
  scenario_params: {},
  global_action_id: null,
  base_donor_uid: null,
  base_donor_ts: null,
  routing: null,
};

/**
 * The seed for a device that has no sim block yet.
 *
 * A FUNCTION, not a constant: `sim_end_time` is relative to now, and a module
 * evaluated once at page load froze it. A tab open for an hour then offered
 * every new simulation an end time fifty minutes in the past — enable it, and
 * it never ticks at all, because the simulator stops a run whose end time has
 * already gone by.
 */
export const makeDefaultSim = (): SimSettings => ({
  ...DEFAULT_SIM,
  sim_end_time: new Date(Date.now() + SIM_END_DEFAULT_MS).toISOString(),
});

/** How long a freshly seeded simulation runs before stopping itself. */
export const SIM_END_DEFAULT_MS = 600_000; // 10 minutes
